// @ts-nocheck
import React, { useState, useEffect } from 'react'
import styles from './../css/StatusBar.module.css'
import StatusLock from './StatusLock.js'
import TargetSummaryBar from './TargetSummaryBar.js'
import apiFetch from '../../../api/apiFetch'
import { StatusBarContext } from './StatusBarContext'
import { useAuth } from '../../../context/AuthContext'
import { usePermissions } from '../../../context/PermissionsContext'
import { TYPE_ORDER } from '../targetingUtils'
import targetingDefaultPerTargetLimits from '../../../lookup/admin/targeting/targetingDefaultPerTargetLimits.json'
import Icon from '../../../components/Icon/Icon';

// Resolve a configured limit value into an absolute count.
// In 'percent' mode: applies the percentage against the baseline.
// In 'static' mode: uses the value directly as a delta (calls) or absolute cap (everything else).
// callMode 'add' = upper call ceiling, 'sub' = lower call floor.
function resolveLimit(configuredValue, baselineCount, limitType, callMode = null) {
    const v = parseInt(configuredValue);
    if (!v || v <= 0) return 0;
    if (limitType === 'static') {
        if (callMode === 'add') return baselineCount + v;
        if (callMode === 'sub') return baselineCount - v;
        return v;
    }
    if (callMode === 'add')  return baselineCount + Math.ceil((v / 100) * baselineCount);
    if (callMode === 'sub')  return baselineCount - Math.ceil((v / 100) * baselineCount);
    return Math.ceil((v / 100) * baselineCount);
}

function StatusBar(props) {
    const permissions = usePermissions()
    const { user } = useAuth()
    const [statusTable, setStatusTable] = useState()
    const [collapsed, setCollapsed] = useState(true)
    const [confirmAction, setConfirmAction] = useState(null) // { index, label, colorKey }

    useEffect(() => {
        apiFetch('/api/v2/targeting/get-statuses', { team: props.exercise?.TEAM }).then((data) => {
            setStatusTable(data)
        })
    }, [])

    const statusIndex = props.status ? props.status.STATUS_ID : 1

    // Shared computed values lifted from StatusLock / LimitsBar
    const exercise = props.exercise;

    // When true, all guardrail and limit checks are suppressed. Used by TargetingV2 for XREF
    // substitute reps whose exercise has opted out of limits via noLimitsForSubstitute in
    // PER_TARGET_LIMITS. Defaults to false so all non-substitute behaviour is unchanged.
    const bypassLimits = !!props.bypassLimits

    let perTargetLimits;
    if (bypassLimits) {
        perTargetLimits = 0;
    } else if (Array.isArray(exercise)) {
        perTargetLimits = exercise[0].PER_TARGET_LIMITS ? JSON.parse(exercise[0].PER_TARGET_LIMITS) : 0;
    } else {
        perTargetLimits = exercise?.PER_TARGET_LIMITS ? JSON.parse(exercise.PER_TARGET_LIMITS) : 0;
    }

    const team = props.team ?? (Array.isArray(exercise) ? exercise[0]?.TEAM : exercise?.TEAM);
    const fallbackConfig = targetingDefaultPerTargetLimits.team[team]?.defaultTargetOptions ?? null;
    const activeTargets = perTargetLimits
        ? Object.entries(perTargetLimits).filter(([k, v]) => v?.isVisible && k !== 'NT').map(([k]) => k)
        : fallbackConfig
            ? TYPE_ORDER.filter(t => fallbackConfig[t]?.isVisible && t !== 'NT')
            : ['A', 'B', 'C'];

    const totalNumberOfTargetsForTerritory = Object.entries(props.status?.typeCounts || {}).filter(([k]) => k !== 'NT').reduce((sum, [, v]) => sum + (v?.final ?? 0), 0);
    const startTotalNumberOfTargetsForTerritory = Object.entries(props.status?.typeCounts || {}).filter(([k]) => k !== 'NT').reduce((sum, [, v]) => sum + (v?.current ?? 0), 0);
    const startTotalNumberOfCallsForTerritory = Object.values(props.status?.typeCounts || {}).reduce((sum, v) => sum + (v?.startCall ?? 0), 0);

    const limitType = (Array.isArray(exercise) ? exercise[0]?.LIMIT_TYPE : exercise?.LIMIT_TYPE) ?? 'percent';

    // Churn counts: v2 passes a client-computed count that excludes NT→A/B/C adds so they don't
    // consume the territory reclassification cap. v1 falls back to the API total (unchanged behaviour).
    const totalChangesInTerritory = props.changesExcludingNTAdds ?? props.perTargetTotals?.totalChangesInTerritory ?? 0;
    const totalProfilesInTerritory = props.perTargetTotals?.totalProfilesInTerritory ?? 0;

    // Territory churn guards: cap/floor on how many profiles can be reclassified (not a size guard)
    // NT profiles are excluded from the denominator — limit is a % of Q1 targeted profiles only
    const maxChangesPerTerritory = !bypassLimits && exercise?.MAX_CHANGES_PER_TERRITORY > 0
        ? resolveLimit(exercise.MAX_CHANGES_PER_TERRITORY, startTotalNumberOfTargetsForTerritory, limitType)
        : 0;
    const minChangesPerTerritory = !bypassLimits && exercise?.MIN_CHANGES_PER_TERRITORY > 0
        ? resolveLimit(exercise.MIN_CHANGES_PER_TERRITORY, startTotalNumberOfTargetsForTerritory, limitType)
        : 0;
    // Upgrade/downgrade territory guardrails
    const maxUpgradesPerTerritory = !bypassLimits && exercise?.MAX_UPGRADES_PER_TERRITORY > 0
        ? resolveLimit(exercise.MAX_UPGRADES_PER_TERRITORY, startTotalNumberOfTargetsForTerritory, limitType)
        : 0;
    const maxDowngradesPerTerritory = !bypassLimits && exercise?.MAX_DOWNGRADES_PER_TERRITORY > 0
        ? resolveLimit(exercise.MAX_DOWNGRADES_PER_TERRITORY, startTotalNumberOfTargetsForTerritory, limitType)
        : 0;
    // Counts: prefer explicitly passed values (v2 computes client-side), fall back to API data
    const totalUpgradesInTerritory = props.upgradeCount ?? props.perTargetTotals?.upgradeCount ?? 0;
    const totalDowngradesInTerritory = props.downgradeCount ?? props.perTargetTotals?.downgradeCount ?? 0;
    const upgradesBySourceType = props.upgradesBySourceType ?? props.perTargetTotals?.upgradesBySourceType ?? {};
    const downgradesBySourceType = props.downgradesBySourceType ?? props.perTargetTotals?.downgradesBySourceType ?? {};
    const maxPlannedCallsPerTerritory = !bypassLimits && exercise?.MAX_CALLS_PER_TERRITORY > 0
        ? resolveLimit(exercise.MAX_CALLS_PER_TERRITORY, startTotalNumberOfCallsForTerritory, limitType, 'add')
        : 0;
    const minPlannedCallsPerTerritory = !bypassLimits && exercise?.MIN_CALLS_PER_TERRITORY > 0
        ? resolveLimit(exercise.MIN_CALLS_PER_TERRITORY, startTotalNumberOfCallsForTerritory, limitType, 'sub')
        : 0;

    // Structured per-target current/final totals — keyed by TARGET_TYPE from the v2 counts table
    const statusTotals = Object.fromEntries(
        Object.entries(props.status?.typeCounts || {}).map(([k, v]) => [
            k, { current: v?.current ?? 0, final: v?.final ?? 0 }
        ])
    );

    const totalPlannedCallsPerTarget = (target) => {
        if (props.perTargetTotals?.plannedCallCounts) {
            const countOfPlannedCalls = props.perTargetTotals.plannedCallCounts
                .filter((v) => v.TARGET_VALUE == target)
                .map((v) => v.COUNT_OF_PLANNED_CALLS);
            return countOfPlannedCalls[0];
        }
        return 0;
    }

    const totalPlannedCallsForTerritory = () => {
        if (props.perTargetTotals?.plannedCallCounts) {
            const countOfPlannedCalls = props.perTargetTotals.plannedCallCounts
                .map((v) => v.COUNT_OF_PLANNED_CALLS);
            return countOfPlannedCalls.reduce((a, b) => a + b, 0);
        }
        return 0;
    }

    // Per-target and per-call limit data — keyed by target type, computed once here
    let perTargetLimitData = {};
    if (perTargetLimits !== 0) {
        // Build outflow lookup: how many profiles are actively moving AWAY FROM each source type
        const outflowByType = {};
        if (props.perTargetTotals?.changesBySourceType) {
            for (const row of props.perTargetTotals.changesBySourceType) {
                outflowByType[row.SOURCE_TYPE] = row.COUNT_OF_CHANGES;
            }
        }
        for (const targetType of Object.keys(perTargetLimits)) {
            const limitConfig = perTargetLimits[targetType];
            if (typeof limitConfig !== 'object' || limitConfig === null) continue;
            if (parseInt(limitConfig.maxChanges) <= 0 && parseInt(limitConfig.minRequired) <= 0) continue;
            const currentTotal = props.status?.typeCounts?.[targetType]?.current ?? 0;
            const finalTotal = props.status?.typeCounts?.[targetType]?.final ?? 0;
            const outflowCount = outflowByType[targetType] ?? 0;
            // Outflow cap: max % (or #) of type-T profiles that can be reclassified away from T
            const maxOutflow = resolveLimit(limitConfig.maxChanges, currentTotal, limitType);
            // Bucket floor: min count that must remain in type T
            const minRequired = parseInt(limitConfig.minRequired) > 0
                ? (limitType === 'static'
                    ? parseInt(limitConfig.minRequired)
                    : currentTotal - Math.ceil((parseInt(limitConfig.minRequired) / 100) * currentTotal))
                : 0;
            perTargetLimitData[targetType] = {
                finalTotal,
                outflowCount,
                maxChanges: maxOutflow,
                minRequired,
                exceedsMax: maxOutflow > 0 && outflowCount > maxOutflow,
                belowMin: parseInt(limitConfig.minRequired) > 0 && minRequired > finalTotal,
            };
        }
    }

    let perUpgradeLimitData = {};
    let perDowngradeLimitData = {};
    if (perTargetLimits !== 0) {
        for (const targetType of Object.keys(perTargetLimits)) {
            const limitConfig = perTargetLimits[targetType];
            if (typeof limitConfig !== 'object' || limitConfig === null) continue;
            const currentTotal = props.status?.typeCounts?.[targetType]?.current ?? 0;
            if (parseInt(limitConfig.maxUpgrades) > 0) {
                const maxUpgradeCount = resolveLimit(limitConfig.maxUpgrades, currentTotal, limitType);
                const upgradeCount = upgradesBySourceType[targetType] ?? 0;
                perUpgradeLimitData[targetType] = {
                    upgradeCount,
                    maxUpgrades: maxUpgradeCount,
                    exceedsMax: maxUpgradeCount > 0 && upgradeCount > maxUpgradeCount,
                };
            }
            if (parseInt(limitConfig.maxDowngrades) > 0) {
                const maxDowngradeCount = resolveLimit(limitConfig.maxDowngrades, currentTotal, limitType);
                const downgradeCount = downgradesBySourceType[targetType] ?? 0;
                perDowngradeLimitData[targetType] = {
                    downgradeCount,
                    maxDowngrades: maxDowngradeCount,
                    exceedsMax: maxDowngradeCount > 0 && downgradeCount > maxDowngradeCount,
                };
            }
        }
    }
    let perCallLimitData = {};
    if (perTargetLimits !== 0) {
        // Iterate all configured types so limits show even before any calls are added
        for (const targetType of Object.keys(perTargetLimits)) {
            const limitConfig = perTargetLimits[targetType];
            if (typeof limitConfig !== 'object' || limitConfig === null) continue;
            if (parseInt(limitConfig.maxCalls) <= 0 && parseInt(limitConfig.minCalls) <= 0) continue;
            const startCallTotal = props.status?.typeCounts?.[targetType]?.startCall ?? 0;
            const currentCallCount = totalPlannedCallsPerTarget(targetType) ?? 0;
            const maxCalls = resolveLimit(limitConfig.maxCalls, startCallTotal, limitType, 'add');
            const minCalls = resolveLimit(limitConfig.minCalls, startCallTotal, limitType, 'sub');
            perCallLimitData[targetType] = {
                currentCallCount,
                maxCalls,
                minCalls,
                exceedsMaxCalls: parseInt(limitConfig.maxCalls) > 0 && maxCalls < currentCallCount,
                belowMinCalls: parseInt(limitConfig.minCalls) > 0 && minCalls > currentCallCount,
            };
        }
    }

    // Per-target start call totals — keyed dynamically from the v2 typeCounts map
    const startCallTotals = {};
    for (const [targetType, counts] of Object.entries(props.status?.typeCounts || {})) {
        if (counts?.startCall !== undefined) startCallTotals[targetType] = counts.startCall;
    }

    // Territory churn flags — compare actual reclassification count against churn cap
    const exceedsMaxChangesPerTerritory = maxChangesPerTerritory > 0 && totalChangesInTerritory > maxChangesPerTerritory;
    const belowMinChangesPerTerritory = minChangesPerTerritory > 0 && totalChangesInTerritory < minChangesPerTerritory;
    const exceedsMaxUpgradesPerTerritory = maxUpgradesPerTerritory > 0 && totalUpgradesInTerritory > maxUpgradesPerTerritory;
    const exceedsMaxDowngradesPerTerritory = maxDowngradesPerTerritory > 0 && totalDowngradesInTerritory > maxDowngradesPerTerritory;
    const exceedsMaxPlannedCallsPerTerritory = maxPlannedCallsPerTerritory > 0 && totalPlannedCallsForTerritory() > maxPlannedCallsPerTerritory;
    const belowMinPlannedCallsPerTerritory = minPlannedCallsPerTerritory > 0 && totalPlannedCallsForTerritory() < minPlannedCallsPerTerritory;

    // Per-target limit aggregate flags
    const hasPerTargetLimitErrors = Object.values(perTargetLimitData).some(d => d.exceedsMax || d.belowMin);
    const hasPerPlannedCallLimitErrors = Object.values(perCallLimitData).some(d => d.exceedsMaxCalls || d.belowMinCalls);
    const hasUpgradeDowngradeLimitErrors =
        exceedsMaxUpgradesPerTerritory ||
        exceedsMaxDowngradesPerTerritory ||
        Object.values(perUpgradeLimitData).some(d => d.exceedsMax) ||
        Object.values(perDowngradeLimitData).some(d => d.exceedsMax);

    const contextValue = {
        statusTotals,
        activeTargets,
        startTotalNumberOfTargetsForTerritory,
        startTotalNumberOfCallsForTerritory,
        user: props.user,
        terr: props.terr,
        id: props.id,
        approved: props.approved,
        getTotals: props.getTotals,
        exercise,
        statusIndex,
        totalNumberOfTargetsForTerritory,
        totalChangesInTerritory,
        totalProfilesInTerritory,
        totalUpgradesInTerritory,
        totalDowngradesInTerritory,
        maxChangesPerTerritory,
        minChangesPerTerritory,
        maxUpgradesPerTerritory,
        maxDowngradesPerTerritory,
        maxPlannedCallsPerTerritory,
        minPlannedCallsPerTerritory,
        totalPlannedCallsForTerritory,
        perTargetLimits,
        perTargetLimitData,
        perCallLimitData,
        perUpgradeLimitData,
        perDowngradeLimitData,
        startCallTotals,
        exceedsMaxChangesPerTerritory,
        belowMinChangesPerTerritory,
        exceedsMaxUpgradesPerTerritory,
        exceedsMaxDowngradesPerTerritory,
        exceedsMaxPlannedCallsPerTerritory,
        belowMinPlannedCallsPerTerritory,
        hasPerTargetLimitErrors,
        hasPerPlannedCallLimitErrors,
        hasUpgradeDowngradeLimitErrors,
    }

    const guardrailErrors = [
        exceedsMaxChangesPerTerritory && `Too many reclassifications: ${totalChangesInTerritory} of ${startTotalNumberOfTargetsForTerritory} profiles changed (cap: ${maxChangesPerTerritory}).`,
        belowMinChangesPerTerritory   && `Not enough reclassifications: ${totalChangesInTerritory} of ${startTotalNumberOfTargetsForTerritory} profiles changed (min: ${minChangesPerTerritory}).`,
        exceedsMaxUpgradesPerTerritory && `Too many upgrades: ${totalUpgradesInTerritory} profiles upgraded (cap: ${maxUpgradesPerTerritory}).`,
        exceedsMaxDowngradesPerTerritory && `Too many downgrades: ${totalDowngradesInTerritory} profiles downgraded (cap: ${maxDowngradesPerTerritory}).`,
        exceedsMaxPlannedCallsPerTerritory && `Cannot submit more than ${maxPlannedCallsPerTerritory} planned calls for the territory.`,
        belowMinPlannedCallsPerTerritory   && `Cannot submit less than ${minPlannedCallsPerTerritory} planned calls for the territory.`,
        ...Object.entries(perTargetLimitData).flatMap(([t, d]) => [
            d.exceedsMax && `Too many ${t} reclassifications: ${d.outflowCount} moved away (cap: ${d.maxChanges}).`,
            d.belowMin   && `Too few ${t} targets remaining: ${d.finalTotal} (floor: ${d.minRequired}).`,
        ]),
        ...Object.entries(perCallLimitData).flatMap(([t, d]) => [
            d.exceedsMaxCalls && `Cannot submit more than ${d.maxCalls} planned calls for ${t} targets.`,
            d.belowMinCalls   && `Cannot submit less than ${d.minCalls} planned calls for ${t} targets.`,
        ]),
        ...Object.entries(perUpgradeLimitData).flatMap(([t, d]) => [
            d.exceedsMax && `Too many ${t} upgrades: ${d.upgradeCount} upgraded from ${t} (cap: ${d.maxUpgrades}).`,
        ]),
        ...Object.entries(perDowngradeLimitData).flatMap(([t, d]) => [
            d.exceedsMax && `Too many ${t} downgrades: ${d.downgradeCount} downgraded from ${t} (cap: ${d.maxDowngrades}).`,
        ]),
    ].filter(Boolean)

    // --- Collapsed action buttons ---
    const sl = permissions.targeting.search.statusLocks
    const approved = props.approved || false

    const showSubmitBtn = !approved && (
        (sl.general.oneTwo.includes(user.role) && (statusIndex === 1 || statusIndex === 2)) ||
        (sl.general.threeFive.includes(user.role) && (statusIndex === 3 || statusIndex === 5))
    )
    const showApproveRejectBtns = !approved && sl.admin.four.includes(user.role) && statusIndex === 4

    const enableSubmitBtn = (
        sl.canSubmitFromOpen.includes(user.role)
        && !exceedsMaxChangesPerTerritory
        && !belowMinChangesPerTerritory
        && !exceedsMaxUpgradesPerTerritory
        && !exceedsMaxDowngradesPerTerritory
        && !exceedsMaxPlannedCallsPerTerritory
        && !belowMinPlannedCallsPerTerritory
        && !hasPerTargetLimitErrors
        && !hasPerPlannedCallLimitErrors
        && !hasUpgradeDowngradeLimitErrors
    ) || user.role === 'ADMIN'

    const enableApproveBtn = (
        !exceedsMaxChangesPerTerritory
        && !belowMinChangesPerTerritory
        && !exceedsMaxUpgradesPerTerritory
        && !exceedsMaxDowngradesPerTerritory
        && !exceedsMaxPlannedCallsPerTerritory
        && !belowMinPlannedCallsPerTerritory
        && !hasPerTargetLimitErrors
        && !hasPerPlannedCallLimitErrors
        && !hasUpgradeDowngradeLimitErrors
    )

    const submitToIndex = (
        sl.submitToUnderReview.roles.includes(user.role) ||
        sl.submitToUnderReview.teams.includes(user.team)
    ) ? 3 : 4

    const emailRequest = (index) => {
        apiFetch('/api/v2/targeting/get-status-by-id', { statusID: index, team: exercise?.TEAM }).then((st) => {
            apiFetch('/api/v2/global/change-request', {
                REQUEST_TYPE: 'IMP TgtEx Submit',
                EXERCISE_ID: props.id,
                EXERCISE_TERR: props.terr,
                STATUS_ID: index,
                STATUS_NAME: st.STATUS,
                STATUS_DESCRIPTION: st.DESCRIPTION,
                REQUESTOR_ID: user.email,
                REQUESTOR_NAME: user.userName,
                REQUESTOR_TEAM: user.team,
                REQUESTOR_TERR: user.terr,
            })
        })
    }

    const submitStatus = (index) => {
        setConfirmAction(null)
        apiFetch('/api/v2/targeting/submit-status', {
            statusID: index,
            ID: exercise?.TARGETING_EXERCISE_ID,
            terr: props.terr,
            username: user.userName,
        }).then(() => { props.getTotals() })
        if ([4, 5, 6].includes(index)) emailRequest(index)
    }

    const confirmColorClass = confirmAction?.colorKey === 'reject'
        ? styles.modalConfirmReject
        : confirmAction?.colorKey === 'approve'
            ? styles.modalConfirmApprove
            : styles.modalConfirmSubmit

    const confirmModal = confirmAction && (
        <div className={styles.modalOverlay} onClick={() => setConfirmAction(null)}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <p className={styles.modalTitle}>Are you sure?</p>
                <p className={styles.modalBody}>This will {confirmAction.label.toLowerCase()} the targeting exercise for this territory.</p>
                <div className={styles.modalActions}>
                    <button className={styles.modalCancel} onClick={() => setConfirmAction(null)}>Cancel</button>
                    <button className={[styles.modalConfirm, confirmColorClass].join(' ')} onClick={() => submitStatus(confirmAction.index)}>{confirmAction.label}</button>
                </div>
            </div>
        </div>
    )

    const statusColorClass =
        statusIndex === 1 ? styles.statusOpen :
        statusIndex === 2 ? styles.statusInProgress :
        statusIndex === 3 ? styles.statusReview :
        statusIndex === 4 ? styles.statusPending :
        statusIndex === 5 ? styles.statusRejected :
        statusIndex === 6 ? styles.statusApproved : ''

    const currentStatusLabel = statusTable && props.status ? statusTable[statusIndex - 1]?.STATUS ?? '' : ''

    return (
        <StatusBarContext.Provider value={contextValue}>
            {confirmModal}
            <div className={[styles.statusBarWrapper, collapsed ? styles.statusBarCollapsed : ''].join(' ')}>
                <button
                    className={styles.collapseToggle}
                    onClick={() => setCollapsed(c => !c)}
                    title={collapsed ? 'Expand status bar' : 'Collapse status bar'}
                >
                    {collapsed
                        ? <><span aria-hidden="true">⌄</span>{props.lock && <Icon name="lock" className={styles.collapsedLockIcon} />}<span className={[styles.collapsedBadge, statusColorClass].join(' ')}>{currentStatusLabel}</span><span className={styles.collapsedHint}>— expand to view guardrails &amp; submission status</span>{guardrailErrors.map((e, i) => <span key={i} className={styles.collapsedGuardrailWarn}>⚠ {e}</span>)}</>
                        : <span aria-hidden="true">⌃</span>
                    }
                </button>
                {collapsed && (showSubmitBtn || showApproveRejectBtns) && (
                    <div className={styles.collapsedActions}>
                        {showSubmitBtn && (
                            <button
                                className={[styles.collapsedActionBtn, styles.collapsedSubmit, !enableSubmitBtn ? styles.collapsedActionDisabled : ''].join(' ')}
                                onClick={() => { if (enableSubmitBtn) setConfirmAction({ index: submitToIndex, label: 'Submit', colorKey: 'submit' }) }}
                                disabled={!enableSubmitBtn}
                            >Submit</button>
                        )}
                        {showApproveRejectBtns && (<>
                            <button
                                className={[styles.collapsedActionBtn, styles.collapsedReject].join(' ')}
                                onClick={() => setConfirmAction({ index: 5, label: 'Reject', colorKey: 'reject' })}
                            >Reject</button>
                            <button
                                className={[styles.collapsedActionBtn, styles.collapsedApprove, !enableApproveBtn ? styles.collapsedActionDisabled : ''].join(' ')}
                                onClick={() => { if (enableApproveBtn) setConfirmAction({ index: 6, label: 'Approve', colorKey: 'approve' }) }}
                                disabled={!enableApproveBtn}
                            >Approve</button>
                        </>)}
                    </div>
                )}
                {!collapsed && (
                    <>
                        <div className={styles.summaryPanel}>
                            <TargetSummaryBar />
                        </div>
                        <div className={styles.statusPanel}>
                            <div className={styles.box}>
                                <div className={styles.statusTopBar}>STATUS</div>
                                <div className={[styles.status, statusColorClass].join(' ')}>{currentStatusLabel || 'Loading...'}</div>
                            </div>
                            <StatusLock />
                        </div>
                    </>
                )}
            </div>
        </StatusBarContext.Provider>
    )
}

export default StatusBar
